import { useQueryClient } from '@tanstack/react-query'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Card,
  CloseIcon,
  Drawer,
  FileList,
  showToast,
  Tag,
  Text,
} from '@mercai/clever'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import {
  formatCurrency,
  formatDate,
  getFileFromUrl,
  objectToFormData,
} from '@/helpers'
import {
  QUERIES_ADMIN,
  useFileSign,
  useShipmentInvoiceDetail,
  useShipmentInvoiceEdit,
} from '@/services'
import { LogisticShipmentProps, ShipmentInvoiceProps } from '@/types'


import { ShipmentInvoiceDrawerShowProps } from './drawer-show.type'
import { formatFilesToFileListFiles } from './drawer-show.constants'
import styles from './drawer-show.module.css'

import { shipmentInvoiceStatusMachine } from '../../helpers'

export const ShipmentInvoiceDrawerShow = ({
  onHandleOpen,
  uuid,
}: ShipmentInvoiceDrawerShowProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [values, setValues] = useState<Partial<ShipmentInvoiceProps>>({})

  const [salesOrderShpments, setSalesOrderShipments] = useState<
    LogisticShipmentProps[]
  >([])
  const [invoicePdfFile, setInvoicePdfFile] = useState<File[]>([])
  const [invoiceXmlFile, setInvoiceXmlFile] = useState<File[]>([])
  const [proofFile, setProofFile] = useState<File[]>([])

  const showInvoiceTransactionInformation =
    values.status === 'paid' || values.status === 'cancelled'

  const { mutateAsync: mutateAsyncFileSign } = useFileSign()

  const { mutateAsync, isPending } = useShipmentInvoiceEdit(uuid)

  const queryClient = useQueryClient()

  const getSignedUrl = async (url: string) => {
    if (!url) return ''

    if (!url.includes('http')) {
      const response = await mutateAsyncFileSign({
        filePath: url || '',
      })

      if (!response?.data?.signedUrl) return ''

      return response?.data?.signedUrl || ''
    }

    return url
  }

  const { data } = useShipmentInvoiceDetail(uuid)

  const onClose = () => {
    onHandleOpen()
    setValues({})
    setInvoicePdfFile([])
    setInvoiceXmlFile([])
    setProofFile([])
    setSalesOrderShipments([])
  }

  const downloadFileFromUrl = async (
    url: string,
  ): Promise<File | undefined> => {
    try {
      const signedUrl = await getSignedUrl(url)

      const file = await getFileFromUrl(signedUrl)

      return file
    } catch (error) {
      console.error(error)
    }
  }

  const populateValues = async (dataParam: ShipmentInvoiceProps) => {
    setValues(dataParam)
    setSalesOrderShipments(dataParam.shipments?.map((val) => val) || [])

    if (dataParam?.invoicePdfFile) {
      const file = await downloadFileFromUrl(dataParam.invoicePdfFile)
      if (file) setInvoicePdfFile([file])
    }

    if (dataParam?.invoiceXmlFile) {
      const file = await downloadFileFromUrl(dataParam.invoiceXmlFile)
      if (file) setInvoiceXmlFile([file])
    }

    if (dataParam?.transactions?.[0]?.proofPdfFile) {
      const file = await downloadFileFromUrl(
        dataParam.transactions?.[0]?.proofPdfFile,
      )
      if (file) setProofFile([file])
    }
  }

  const onUpdateToReview = async () => {
    const response = await mutateAsync(
      objectToFormData({
        status: 'request_review',
        transportCompanyUuid: values?.transportCompany?.uuid,
      }),
    )

    if (response.success) {
      setValues({})
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module],
      })
      showToast({
        position: 'bottom-left',
        variant: 'success',
        title: t('tabShipmentInvoiceDrawerFormEditSuccessTitle'),
        content: t('tabShipmentInvoiceDrawerFormEditSuccessDescription'),
      })
      return null
    }

    showToast({
      position: 'bottom-left',
      variant: 'error',
      content:
        response.message ||
        t('tabShipmentInvoiceDrawerFormEditSuccessDescription'),
    })
  }

  const statuses: Record<string, ReactNode> = {
    created: (
      <Tag variant="basic" model="light">
        {tEnums('shipment_invoice_status_created')}
      </Tag>
    ),
    pending: (
      <Tag variant="warning" model="light">
        {tEnums('shipment_invoice_status_pending')}
      </Tag>
    ),
    request_review: (
      <Tag variant="info" model="light">
        {tEnums('shipment_invoice_status_request_review')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {tEnums('shipment_invoice_status_paid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {tEnums('shipment_invoice_status_cancelled')}
      </Tag>
    ),
  }

  useEffect(() => {
    if (data?.data) {
      populateValues(data?.data)
    } else {
      setValues({})
      setInvoicePdfFile([])
      setInvoiceXmlFile([])
      setProofFile([])
      setSalesOrderShipments([])
    }
  }, [data?.data])

  return (
    <Drawer isOpen={!!uuid} onHandleOpen={onClose} className={styles.drawer}>
      <div className={styles.header}>
        <div>
          <Text variant="title-large" className={styles['header-title']}>
            {t('tabShipmentInvoiceDrawerShowTitle')}
          </Text>

          <Text variant="text-small" className={styles['header-description']}>
            {t('tabShipmentInvoiceDrawerShowDescription')}
          </Text>
        </div>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles['content-grid']}>
          <div className={styles['main-column']}>
            <div className={styles['main-info-card']}>
              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowTransportCompanyLabel')}:
                  </strong>
                </Text>
                <Text className={styles.value}>
                  {values.transportCompany?.name || '-'}
                </Text>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>{t('tabShipmentInvoiceDrawerShowTypeLabel')}:</strong>
                </Text>
                <div className={styles.value}>
                  {values?.type
                    ? tEnums(`shipment_invoice_type_${values?.type}`)
                    : '-'}
                </div>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowPaymentPriorityLabel')}:
                  </strong>
                </Text>
                <div className={styles.value}>
                  {values?.paymentPriority
                    ? tEnums(
                      `shipment_invoice_payment_priority_${values?.paymentPriority}`,
                    )
                    : '-'}
                </div>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowStatusLabel')}:
                  </strong>
                </Text>
                <div className={styles.value}>
                  {statuses[values.status || 'pending']}
                </div>
              </div>

              {showInvoiceTransactionInformation && (
                <>
                  <div className={styles.row}>
                    <Text className={styles.label}>
                      <strong>
                        {t(
                          'tabShipmentInvoiceDrawerShowTransactionPaymentMethodLabel',
                        )}
                        :
                      </strong>
                    </Text>
                    <Text className={styles.value}>
                      {values?.transactions?.[0]?.destinationBank || '-'}
                    </Text>
                  </div>

                  <div className={styles.row}>
                    <Text className={styles.label}>
                      <strong>
                        {t(
                          'tabShipmentInvoiceDrawerShowTransactionPaidAtLabel',
                        )}
                        :
                      </strong>
                    </Text>
                    <Text className={styles.value}>
                      {values?.transactions?.[0]?.paidAt
                        ? formatDate(values?.transactions?.[0]?.paidAt)
                        : '-'}
                    </Text>
                  </div>
                </>
              )}

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowValueLabel')}:
                  </strong>
                </Text>
                <Text className={styles.value}>
                  {formatCurrency(
                    values?.value || '',
                    'es-MX',
                    values.currency || 'MXN',
                    DECIMAL_PLACES_DEFAULT,
                  )}
                </Text>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowExpectedPaymentDateLabel')}:
                  </strong>
                </Text>
                <Text className={styles.value}>
                  {values?.expectedPaymentDate
                    ? formatDate(values.expectedPaymentDate)
                    : '-'}
                </Text>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t('tabShipmentInvoiceDrawerShowObservationLabel')}:
                  </strong>
                </Text>
                <Text className={styles.value}>
                  {values?.observation || '-'}
                </Text>
              </div>

              <div className={styles.row}>
                <Text className={styles.label}>
                  <strong>
                    {t(
                      'tabShipmentInvoiceDrawerShowInvoiceExternalReferenceLabel',
                    )}
                    :
                  </strong>
                </Text>
                <Text className={styles.value}>
                  {values?.invoiceExternalReference || '-'}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles['side-column']}>
            <section className={styles.section}>
              <Text variant="title-base" className={styles['section-title']}>
                {t(
                  'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceSaleOrdersLabel',
                )}
              </Text>

              {salesOrderShpments?.length > 0 ? (
                <div className={styles['card-sale-order']}>
                  {salesOrderShpments.map((shipment) => (
                    <Card
                      className={styles['card-sale-order-item']}
                      key={shipment?.uuid}
                    >
                      <div className={styles['card-sale-order-item-left']}>
                        <Text
                          variant="title-base"
                          className={styles['card-sale-order-item-left-title']}
                        >
                          {t('tabShipmentInvoiceDrawerShowSaleOrderLabel')} #
                          {shipment?.order}
                        </Text>
                        <Text variant="text-xsmall">
                          {t('tabShipmentInvoiceDrawerShowFirstMileCostLabel')}
                          {formatCurrency(
                            shipment?.firstMileCost || '0',
                            'es-MX',
                            'USD',
                            DECIMAL_PLACES_DEFAULT,
                          )}
                        </Text>
                        <Text variant="text-xsmall">
                          {t('tabShipmentInvoiceDrawerShowLastMileCostLabel')}
                          {formatCurrency(
                            shipment?.lastMileCost || '0',
                            'es-MX',
                            'USD',
                            DECIMAL_PLACES_DEFAULT,
                          )}
                        </Text>
                        <Text variant="text-xsmall">
                          {t('tabShipmentInvoiceDrawerShowCustomsCostLabel')}
                          {formatCurrency(
                            shipment?.customsCost || '0',
                            'es-MX',
                            'USD',
                            DECIMAL_PLACES_DEFAULT,
                          )}
                        </Text>
                        <Text variant="text-xsmall">
                          {t('tabShipmentInvoiceDrawerShowExtraCostLabel')}
                          {formatCurrency(
                            shipment?.extraCost || '0',
                            'es-MX',
                            'USD',
                            DECIMAL_PLACES_DEFAULT,
                          )}
                        </Text>
                      </div>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className={styles['card-sale-order']}>
                  <Text>
                    {t('tabShipmentInvoiceDrawerShowSaleOrdersEmpty')}
                  </Text>
                </div>
              )}
            </section>

            {showInvoiceTransactionInformation && (
              <section className={styles.section}>
                <Text className={styles['section-title']}>
                  {t('tabShipmentInvoiceDrawerShowTransactionProofFileLabel')}
                </Text>
                <FileList
                  data={formatFilesToFileListFiles(proofFile)}
                  className={styles.file}
                />
              </section>
            )}

            <section className={styles.section}>
              <Text className={styles['section-title']}>
                {t('tabShipmentInvoiceDrawerShowInvoicePdfFileLabel')}
              </Text>
              <FileList
                data={formatFilesToFileListFiles(invoicePdfFile)}
                className={styles.file}
              />
            </section>

            <section className={styles.section}>
              <Text className={styles['section-title']}>
                {t('tabShipmentInvoiceDrawerShowInvoiceXmlFileLabel')}
              </Text>
              <FileList
                data={formatFilesToFileListFiles(invoiceXmlFile)}
                className={styles.file}
              />
            </section>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          {t('drawerCancel')}
        </Button>
        {shipmentInvoiceStatusMachine[values?.status || 'created'].includes(
          'request_review',
        ) ? (
          <Button
            onClick={onUpdateToReview}
            disabled={isPending}
            isActive={!isPending}
            variant="secondary"
          >
            {t('drawerRequestReview')}
          </Button>
        ) : null}
      </div>
    </Drawer>
  )
}
